import { SimpleGeometry } from 'ol/geom';
import { Coordinate } from 'ol/coordinate';
import turfBuffer from '@turf/buffer';
import { polygon as turfPolygon, lineString as turfLineString, featureCollection } from '@turf/helpers';
import booleanIntersects from '@turf/boolean-intersects';
import difference from '@turf/difference';
import { convertProjection3857 } from '../../../utils';

class PolygonCutter {
  // Default buffer in Web Mercator units (~1 meters)
  // This creates a reasonable separation to prevent polygon boundaries from touching
  private readonly DEFAULT_BUFFER_DISTANCE = 1;

  /**
   * Cut a polygon into two separate polygons using a linestring
   * Uses Turf.js to split the polygon along the cutting line and create buffered separation
   * @param geometry The polygon geometry to cut (in Web Mercator projection)
   * @param cuttingLineCoordinates The coordinates of the cutting line (in Web Mercator projection)
   * @param bufferDistance Optional buffer distance in meters (defaults to 1 meter)
   * @returns A multipolygon shape, or undefined if cutting failed + error message
   */
  cutPolygon(
    geometry: SimpleGeometry,
    cuttingLineCoordinates: Coordinate[],
    bufferDistance?: number,
  ): { shape: Coordinate[][][] | undefined; error?: string } {
    const buffer = bufferDistance ?? this.DEFAULT_BUFFER_DISTANCE;

    if (cuttingLineCoordinates.length < 2) {
      return { shape: undefined, error: 'Cutting line must have at least 2 points' };
    }

    // Convert OpenLayers geometry to Turf format (lat/lon)
    const coordinates: Coordinate[][] = geometry.getCoordinates();
    const outerRing = coordinates[0];
    const holes = coordinates.slice(1);

    if (outerRing.length < 4) {
      return { shape: undefined, error: 'Polygon must have at least 3 vertices' };
    }

    // Convert to lat/lon for Turf operations
    const outerRingLatLon = outerRing.map((coord) => convertProjection3857(coord).toReversed());
    const holesLatLon = holes.map((hole) => hole.map((coord) => convertProjection3857(coord).toReversed()));
    const cuttingLineLatLon = cuttingLineCoordinates.map((coord) => convertProjection3857(coord).toReversed());

    // Create Turf geometries
    const originalPolygon = turfPolygon([outerRingLatLon, ...holesLatLon]);
    const cuttingLine = turfLineString(cuttingLineLatLon);

    // Check if the cutting line intersects the polygon
    if (!booleanIntersects(cuttingLine, originalPolygon)) {
      return { shape: undefined, error: 'Cutting line does not intersect polygon' };
    }

    // Convert buffer distance from meters to degrees (approximate at mid-latitude)
    // At ~40° latitude, 1 meter ≈ 0.000009 degrees
    const bufferInDegrees = buffer / 111320; // 111320 meters per degree at equator

    // Create a buffered cutting line
    const bufferedLine = turfBuffer(cuttingLine, bufferInDegrees, { units: 'degrees' });

    if (!bufferedLine) {
      return { shape: undefined, error: 'Failed to create buffered cutting line' };
    }

    // Split the polygon by removing the buffer zone
    // This creates two polygons separated by the buffer distance
    const splitResult = difference(featureCollection([originalPolygon, bufferedLine]));

    if (!splitResult) {
      return { shape: undefined, error: 'Failed to split polygon' };
    }

    // The difference operation may return a Polygon or MultiPolygon
    if (splitResult.geometry.type === 'Polygon') {
      // If we get a single polygon, the line didn't fully split it
      return { shape: undefined, error: 'Cutting line did not fully split polygon into two parts' };
    }

    if (splitResult.geometry.type !== 'MultiPolygon') {
      return { shape: undefined, error: 'Unexpected geometry type after split' };
    }

    return { shape: splitResult.geometry.coordinates };
  }
}

export const polygonCutter = new PolygonCutter();
